import React from 'react'
import moment from 'moment'
import Grid from '@mui/material/Grid'
import WeekPicker from '@/components/weekPicker/WeekPicker'
import { startOfISOWeek } from 'date-fns'
import Calendar from '@/components/calendar/Calendar'
import utils from '@/components/calendar/utils'
import ColorExplonationBox from '@/components/calendar/ColorExplonationBox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import DropDown from '@/components/dropDown/DropDown'
import PopupDialog from '@/components/popupDialog/PopupDialog'
import ActionButton from '@/components/buttons/ActionButton'
import SendIcon from '@mui/icons-material/Send'
import Skeleton from '@mui/material/Skeleton'
import {
  IExistingScaleInterval,
  ICellConfig,
  ECellColor,
  getTimeslotColor,
  EBadgeType,
} from '@/components/calendar/calendarTypes'
import {
  useLazyGetUserQuery,
  useLazySubmitTimeslotsQuery,
} from '@/store/apis/users'
import {
  useLazyAddTimeslotsQuery,
  useLazyUpdateTimeslotQuery,
  useLazyDeleteTimeslotsQuery,
  ETimeslotIdTypes,
} from '@/store/apis/timeslots'
import { useLazyGetSchemaQuery } from '@/store/apis/schemas'
import { ETimeslotType } from '@/store/apis/timeslots'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface IUserSchemaProps {
  isSchemaCreator?: boolean
}

const UserSchema: React.FC<IUserSchemaProps> = ({ isSchemaCreator }) => {
  const urlParams = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [
    getSchemaQuery,
    { data: schemaData, isFetching: isFetchingSchemaData },
  ] = useLazyGetSchemaQuery()
  const [getUserQuery, { data: userData, isFetching: isFetchingUser }] =
    useLazyGetUserQuery()
  const [submitTimeslotsQuery, { isFetching: isSubmittingTimeslots }] =
    useLazySubmitTimeslotsQuery()
  const [addTimeslotsQuery] = useLazyAddTimeslotsQuery()
  const [updatTimeslotQuery] = useLazyUpdateTimeslotQuery()
  const [deleteTimeslotsQuery] = useLazyDeleteTimeslotsQuery()
  const [firstDay, setFirstDay] = React.useState<moment.Moment | undefined>()
  const [timeslots, setTimeslots] = React.useState<IExistingScaleInterval[]>([])
  const [underlyingCellConfigs, setUnderlyingCellConfigs] = React.useState<
    ICellConfig[]
  >([])
  const [isEditable, setIsEditable] = React.useState<boolean>(!isSchemaCreator)
  const [isEditPopoverOpen, setIsEditPopoverOpen] =
    React.useState<boolean>(false)
  const schemaId = Number(urlParams.schemaId)

  const getUser = () => {
    if (isSchemaCreator && urlParams.userId !== undefined) {
      getUserQuery({ id: Number(urlParams.userId) })
    } else {
      getUserQuery({ id: Number(localStorage.getItem('userId')) })
    }
  }

  const getUserId = (): number => {
    if (isSchemaCreator && urlParams.userId !== undefined) {
      return Number(urlParams.userId)
    } else {
      return Number(localStorage.getItem('userId'))
    }
  }

  React.useEffect(() => {
    getUser()
    if (urlParams?.schemaId) {
      getSchemaQuery({ id: schemaId })
    }
  }, [getUserQuery, getSchemaQuery, urlParams])

  React.useEffect(() => {
    if (schemaData && !firstDay) {
      const nowDate = new Date()
      const schemaDate = new Date(schemaData.schema.schema.startDate)
      const date = nowDate < schemaDate ? schemaDate : nowDate
      setFirstDay(moment(date).startOf('week'))
    }
  }, [schemaData, firstDay])

  React.useEffect(() => {
    if (schemaData && userData) {
      let existingCells: ICellConfig[] = []
      schemaData.schema.timeslots.map((timeslot) => {
        const intervals = utils.getIntervalsByDuration(
          30,
          moment(timeslot.startTimestamp),
          moment(timeslot.endTimestamp),
        )
        existingCells = existingCells.concat(
          intervals.map((interval, index) => ({
            startTimestamp: interval.start,
            color: getTimeslotColor(timeslot),
            disabled: timeslot.type === ETimeslotType.Disabled,
            disableHover: true,
            comment: index === 0 && timeslot.comment,
            badge: timeslot.comment && {
              type: EBadgeType.Scene,
              contents: [
                {
                  label: timeslot.comment,
                  comment: '',
                },
              ],
            },
          })),
        )
      })
      setUnderlyingCellConfigs(existingCells)

      const userSchemaTimeslots = userData.timeslots.filter(
        (timeslot) => timeslot.schemaId === schemaData.schema.schema.id,
      )
      const existingTimeslots = userSchemaTimeslots.map(
        (timeslot): IExistingScaleInterval => ({
          id: timeslot.id,
          start: moment(timeslot.startTimestamp),
          end: moment(timeslot.endTimestamp),
          color: ECellColor.User,
          comment: timeslot.comment,
        }),
      )
      setTimeslots(existingTimeslots)
    }
  }, [userData, schemaData])

  const handleAddTimeslot = async (
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
  ): Promise<void> => {
    if (schemaData) {
      await addTimeslotsQuery({
        parentType: ETimeslotIdTypes.User,
        parentId: getUserId(),
        schemaId: schemaData.schema.schema.id,
        timeslots: [
          {
            startTimestamp: startTimestamp.toISOString(),
            endTimestamp: endTimestamp.toISOString(),
            comment,
          },
        ],
      })
      getUser()
    }
  }

  const handleUpdateTimeslot = async (
    id: number,
    startTimestamp: Date,
    endTimestamp: Date,
    comment: string,
  ): Promise<void> => {
    if (schemaData) {
      await updatTimeslotQuery({
        id,
        parentType: ETimeslotIdTypes.User,
        parentId: getUserId(),
        timeslot: {
          startTimestamp: startTimestamp.toISOString(),
          endTimestamp: endTimestamp.toISOString(),
          comment,
        },
      })
      getUser()
    }
  }

  const handleDeleteTimeslots = async (ids: number[]): Promise<void> => {
    await deleteTimeslotsQuery({
      schemaId: schemaData.schema.schema.id,
      ids,
    })
    getUser()
  }

  const handleSubmitTimeslots = async (): Promise<void> => {
    if (schemaData) {
      await submitTimeslotsQuery({
        id: getUserId(),
        schemaId: schemaData.schema.schema.id,
      })
      getUser()
    }
  }

  const { filledInTimeslots } = userData?.schemas.find(
    (schema) => schema.id === schemaId,
  ) ?? { filledInTimeslots: false }

  return (
    <>
      <Grid
        container
        columnSpacing={2}
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs>
          <WeekPicker
            value={firstDay ? firstDay.toDate() : new Date()}
            minDate={
              schemaData
                ? startOfISOWeek(new Date(schemaData.schema.schema.startDate))
                : startOfISOWeek(new Date())
            }
            maxDate={
              schemaData
                ? new Date(schemaData.schema.schema.endDate)
                : new Date()
            }
            onChange={(value) => setFirstDay(moment(value).startOf('week'))}
          />
        </Grid>
        {isSchemaCreator && (
          <Grid item xs>
            <Grid
              container
              spacing={4}
              alignItems="center"
              justifyContent="center"
              direction="column"
            >
              <Grid item xs>
                <DropDown
                  label={t('schema')}
                  width="300px"
                  defaultValue={
                    schemaData && {
                      id: schemaData.schema.schema.id,
                      label: schemaData.schema.schema.name,
                    }
                  }
                  values={userData?.schemas.map((schema) => ({
                    id: schema.id,
                    label: schema.name,
                  }))}
                  isLoading={isFetchingUser}
                  onChange={(newSchema) =>
                    navigate(
                      `/users/${userData?.user.id}/schemas/${newSchema.id}`,
                    )
                  }
                />
              </Grid>
              <Grid item xs>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={isEditable}
                      onClick={() => {
                        if (!isEditable) {
                          setIsEditPopoverOpen(true)
                        } else {
                          setIsEditable(false)
                        }
                      }}
                    />
                  }
                  label={t('buttons.edit')}
                  labelPlacement="start"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item xs>
          <Grid container columnSpacing={2}>
            <ColorExplonationBox
              title={t('colorExplonation.default')}
              color={ECellColor.Default}
            />
            <ColorExplonationBox
              title={t('colorExplonation.user')}
              color={ECellColor.User}
            />
            <ColorExplonationBox
              title={t('colorExplonation.important')}
              color={ECellColor.Important}
            />
            <ColorExplonationBox
              title={t('colorExplonation.required')}
              color={ECellColor.Required}
            />
            <ColorExplonationBox
              title={t('colorExplonation.disabled')}
              color={ECellColor.Disabled}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container display="flex" direction="column" alignContent="center">
        <Grid item xs={6} display="flex" justifyContent="center">
          {!filledInTimeslots ? (
            <ActionButton
              icon={<SendIcon />}
              isLoading={isSubmittingTimeslots}
              disabled={
                (isSchemaCreator && !isEditable) ||
                isSubmittingTimeslots ||
                filledInTimeslots
              }
              label={t('buttons.submitTimestamps')}
              onClick={handleSubmitTimeslots}
            />
          ) : (
            <>
              {isFetchingUser ? (
                <Skeleton width={200} height={'2em'} />
              ) : (
                <div style={{ height: '2em' }}>
                  <b>
                    <i>{t('buttons.submitedTimestamps')}</i>
                  </b>
                </div>
              )}
            </>
          )}
        </Grid>
        <Grid
          item
          xs={6}
          display="flex"
          justifyContent="center"
          sx={{ marginTop: '14px' }}
        >
          <i>{t('submitExplonation')}</i>
        </Grid>
      </Grid>
      <Calendar
        isLoading={isFetchingSchemaData || isFetchingUser}
        firstDay={firstDay ?? moment()}
        onFirstDayUpdate={(newFirstDay) => setFirstDay(newFirstDay)}
        minDay={
          schemaData ? moment(schemaData.schema.schema.startDate) : undefined
        }
        maxDay={
          schemaData ? moment(schemaData.schema.schema.endDate) : undefined
        }
        createColor={ECellColor.User}
        preview={!isEditable}
        selectedIntervals={timeslots}
        underlyingCellConfigs={underlyingCellConfigs}
        outsideRangeColor={ECellColor.Disabled}
        onIntervalSelect={handleAddTimeslot}
        onIntervalUpdate={handleUpdateTimeslot}
        onIntervalsRemove={handleDeleteTimeslots}
        calendarDayComment={
          schemaData
            ? schemaData.schema.days.map((day) => ({
                date: new Date(day.date),
                comment: day.comment,
              }))
            : []
        }
      />
      <PopupDialog
        isOpen={isEditPopoverOpen}
        title={
          <>
            {t('popups.editUser')}: <b>{userData?.user.firstName}</b>
          </>
        }
        content={t('popups.editUserDescription')}
        isLoading={false}
        onClose={() => {
          setIsEditable(false)
          setIsEditPopoverOpen(false)
        }}
        onConfirm={() => {
          setIsEditable(true)
          setIsEditPopoverOpen(false)
        }}
      />
    </>
  )
}

export default UserSchema
